import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  CircleAlert,
  Mail,
  Phone,
} from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "components/ui/toggle-group";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from "components/ui/field";
import { Alert, AlertTitle, AlertDescription } from "components/ui/alert";
import { Separator } from "components/ui/separator";
import { Spinner } from "components/ui/spinner";
import PageMeta from "../components/PageMeta";
import { contact } from "../content";
import {
  emptyRequest,
  prepareManagementRequest,
  submitManagementRequest,
} from "../request/management";

function TextField({
  name,
  label,
  values,
  errors,
  onChange,
  pending,
  optional = false,
  ...props
}) {
  const id = `request-${name}`;
  return (
    <Field data-invalid={!!errors[name]} data-disabled={pending}>
      <FieldLabel htmlFor={id}>
        {label}
        {optional && <span className="site-optional">Optional</span>}
      </FieldLabel>
      <Input
        id={id}
        name={name}
        value={values[name]}
        onChange={onChange}
        disabled={pending}
        required={!optional}
        aria-invalid={!!errors[name]}
        aria-describedby={errors[name] ? `${id}-error` : undefined}
        {...props}
      />
      {errors[name] && (
        <FieldError id={`${id}-error`}>{errors[name]}</FieldError>
      )}
    </Field>
  );
}

export default function RequestManagement() {
  const [values, setValues] = useState({ ...emptyRequest });
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const [success, setSuccess] = useState(false);
  const busy = useRef(false),
    mounted = useRef(true),
    controller = useRef(null),
    form = useRef(null),
    successHeading = useRef(null),
    errorAlert = useRef(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (success) successHeading.current?.focus();
  }, [success]);
  useEffect(() => {
    if (failure) errorAlert.current?.focus();
  }, [failure]);
  const update = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };
  const onChange = (event) => update(event.target.name, event.target.value);
  async function onSubmit(event) {
    event.preventDefault();
    if (busy.current) return;
    const prepared = prepareManagementRequest(values);
    setErrors(prepared.errors);
    setFailure("");
    if (Object.keys(prepared.errors).length) {
      requestAnimationFrame(() => {
        const invalid = form.current?.querySelector('[aria-invalid="true"]');
        (invalid?.querySelector("button") || invalid)?.focus();
      });
      return;
    }
    busy.current = true;
    setPending(true);
    controller.current = new AbortController();
    try {
      await submitManagementRequest(prepared.payload, {
        signal: controller.current.signal,
      });
      if (mounted.current) setSuccess(true);
    } catch (error) {
      if (mounted.current) setFailure(error.message);
    } finally {
      busy.current = false;
      if (mounted.current) setPending(false);
    }
  }
  const fieldProps = { values, errors, onChange, pending };
  return (
    <>
      <PageMeta
        title="Request management | Owners First"
        description="Tell us about your property and owners corporation. Start a conversation about management with Owners First."
      />
      <section className="site-wrap site-request-layout">
        <div className="site-request-intro">
          <h1>
            Let’s talk about
            <br />
            <span>your property.</span>
          </h1>
          <p>
            Tell us a little about your building and what you’re looking for.
            Our team will be in touch to discuss how we can help.
          </p>
          <div className="site-request-contacts">
            <a href={contact.telephone}>
              <Phone aria-hidden="true" />
              {contact.phone}
            </a>
            <a href={`mailto:${contact.email}`}>
              <Mail aria-hidden="true" />
              {contact.email}
            </a>
          </div>
          <img
            src="/images/interior.jpg"
            alt="A welcoming, sunlit shared apartment lobby"
            loading="lazy"
            width="1792"
            height="1024"
          />
        </div>
        <div className="site-request-panel">
          {success ? (
            <div className="site-request-success">
              <CheckCircle2 aria-hidden="true" />
              <h2 ref={successHeading} tabIndex={-1}>
                Thank you.
                <br />
                We’ll be in touch.
              </h2>
              <p>
                Your management request has been received. Our team will contact
                you to learn more about your property.
              </p>
              <Button size="lg" asChild>
                <Link to="/">
                  Back to home
                  <ArrowUpRight data-icon="inline-end" />
                </Link>
              </Button>
            </div>
          ) : (
            <form
              ref={form}
              onSubmit={onSubmit}
              noValidate
              aria-label="Request management"
              aria-busy={pending}
            >
              <FieldGroup>
                <FieldSet disabled={pending}>
                  <FieldLegend>About you</FieldLegend>
                  <FieldGroup>
                    <TextField
                      name="name"
                      label="Your name"
                      autoComplete="name"
                      maxLength={50}
                      {...fieldProps}
                    />
                    <TextField
                      name="email"
                      label="Email address"
                      type="email"
                      autoComplete="email"
                      maxLength={100}
                      {...fieldProps}
                    />
                    <TextField
                      name="phone"
                      label="Phone number"
                      type="tel"
                      autoComplete="tel"
                      maxLength={20}
                      {...fieldProps}
                    />
                    <Field
                      data-invalid={!!errors.relation}
                      data-disabled={pending}
                    >
                      <FieldTitle id="relation-label">
                        Your relationship to the property
                      </FieldTitle>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="lg"
                        value={values.relation}
                        onValueChange={(value) => update("relation", value)}
                        disabled={pending}
                        aria-labelledby="relation-label"
                        aria-invalid={!!errors.relation}
                        aria-describedby={
                          errors.relation ? "relation-error" : undefined
                        }
                        className="tw-justify-start tw-gap-2 tw-flex-wrap"
                      >
                        {["Owner", "Tenant", "Other"].map((value) => (
                          <ToggleGroupItem
                            key={value}
                            value={value}
                            className="tw-flex-1"
                          >
                            {value}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                      {errors.relation && (
                        <FieldError id="relation-error">
                          {errors.relation}
                        </FieldError>
                      )}
                    </Field>
                  </FieldGroup>
                </FieldSet>
                <Separator />
                <FieldSet disabled={pending}>
                  <FieldLegend>About your property</FieldLegend>
                  <FieldGroup>
                    <TextField
                      name="addressLine1"
                      label="Property address"
                      autoComplete="section-property address-line1"
                      maxLength={100}
                      {...fieldProps}
                    />
                    <TextField
                      name="addressLine2"
                      label="Address line 2"
                      autoComplete="section-property address-line2"
                      maxLength={100}
                      optional
                      {...fieldProps}
                    />
                    <FieldGroup className="sm:tw-grid sm:tw-grid-cols-2">
                      <TextField
                        name="postCode"
                        label="Postcode"
                        inputMode="numeric"
                        autoComplete="section-property postal-code"
                        maxLength={4}
                        {...fieldProps}
                      />
                      <TextField
                        name="numberOfLots"
                        label="Number of lots"
                        inputMode="numeric"
                        maxLength={6}
                        {...fieldProps}
                      />
                    </FieldGroup>
                    <Field
                      data-invalid={!!errors.comments}
                      data-disabled={pending}
                    >
                      <FieldLabel htmlFor="request-comments">
                        What would you like us to know?
                        <span className="site-optional">Optional</span>
                      </FieldLabel>
                      <Textarea
                        id="request-comments"
                        name="comments"
                        rows={5}
                        value={values.comments}
                        onChange={onChange}
                        disabled={pending}
                        maxLength={3000}
                        aria-invalid={!!errors.comments}
                        aria-describedby={
                          errors.comments ? "comments-error" : "comments-hint"
                        }
                      />
                      <FieldDescription id="comments-hint">
                        Anything about your property, your priorities or your
                        current management.
                      </FieldDescription>
                      {errors.comments && (
                        <FieldError id="comments-error">
                          {errors.comments}
                        </FieldError>
                      )}
                    </Field>
                  </FieldGroup>
                </FieldSet>
                {failure && (
                  <Alert variant="destructive" ref={errorAlert} tabIndex={-1}>
                    <CircleAlert />
                    <AlertTitle>Request not confirmed</AlertTitle>
                    <AlertDescription>{failure}</AlertDescription>
                  </Alert>
                )}
                <div className="site-form-submit">
                  <Button type="submit" size="lg" disabled={pending}>
                    {pending ? (
                      <>
                        <Spinner data-icon="inline-start" />
                        Sending request
                      </>
                    ) : (
                      <>
                        Send management request
                        <ArrowUpRight data-icon="inline-end" />
                      </>
                    )}
                  </Button>
                  <p>
                    We use these details to respond to your enquiry.{" "}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="site-inline-link"
                    >
                      Read our privacy policy.
                      <span className="tw-sr-only"> Opens in a new tab.</span>
                    </Link>
                  </p>
                </div>
              </FieldGroup>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
